// src/hooks/useKeyboardShortcuts.ts
// Reusable keyboard shortcut handler with presets for common actions

import { useEffect, useMemo } from "react";

// ============================================
// Types
// ============================================

export interface KeyboardShortcut {
  /** Key to listen for (e.g., 'n', 'r', 'Escape') */
  key: string;

  /** Require Ctrl (Windows/Linux) or Cmd (Mac) */
  ctrl?: boolean;

  /** Require Shift key */
  shift?: boolean;

  /** Require Alt (Windows/Linux) or Option (Mac) */
  alt?: boolean;

  /** Handler function to execute */
  action: () => void;

  /** Description for help/accessibility */
  description?: string;

  /** Whether shortcut is currently enabled (default: true) */
  enabled?: boolean;

  /** Prevent default browser behavior (default: true) */
  preventDefault?: boolean;
}

export interface UseKeyboardShortcutsOptions {
  /** Array of shortcuts to register */
  shortcuts: KeyboardShortcut[];

  /** Global enable/disable for all shortcuts (default: true) */
  enabled?: boolean;

  /** Scope identifier for debugging */
  scope?: string;

  /** Whether to allow shortcuts while in input fields (default: false) */
  allowInInputs?: boolean;
}

// ============================================
// Main Hook
// ============================================

export const useKeyboardShortcuts = (
  options: UseKeyboardShortcutsOptions
): void => {
  const { shortcuts, enabled = true, scope, allowInInputs = false } = options;

  useEffect(() => {
    if (!enabled || shortcuts.length === 0) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      // Skip if user is typing in an input (unless explicitly allowed)
      if (!allowInInputs) {
        const target = event.target as HTMLElement;
        const tagName = target.tagName.toUpperCase();

        if (
          tagName === "INPUT" ||
          tagName === "TEXTAREA" ||
          tagName === "SELECT" ||
          target.isContentEditable
        ) {
          // Allow Escape key even in inputs
          if (event.key !== "Escape") {
            return;
          }
        }
      }

      // Detect platform for Ctrl/Cmd handling
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const ctrlKey = isMac ? event.metaKey : event.ctrlKey;

      for (const shortcut of shortcuts) {
        // Skip disabled shortcuts
        if (shortcut.enabled === false) {
          continue;
        }

        // Check key match (case-insensitive)
        const keyMatch =
          event.key.toLowerCase() === shortcut.key.toLowerCase() ||
          event.code.toLowerCase() === `key${shortcut.key.toLowerCase()}`;

        // Check modifier keys
        const ctrlMatch = shortcut.ctrl ? ctrlKey : !ctrlKey;
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatch = shortcut.alt ? event.altKey : !event.altKey;

        // console.log("=== shortcut", shortcut, {
        //   eventKey: event.key,
        //   eventCode: event.code,
        //   keyMatch,
        //   ctrlMatch,
        //   shiftMatch,
        //   altMatch,
        // });

        if (keyMatch && ctrlMatch && shiftMatch && altMatch) {
          // Prevent default unless explicitly set to false
          if (shortcut.preventDefault !== false) {
            event.preventDefault();
            event.stopPropagation();
          }

          // Log for debugging if scope is provided
          if (scope && process.env.NODE_ENV === "development") {
            console.debug(
              `[Keyboard:${scope}] Triggered: ${shortcut.description || shortcut.key
              }`
            );
          }

          shortcut.action();
          return;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [shortcuts, enabled, scope, allowInInputs]);
};

// ============================================
// Entity Page Shortcuts Preset
// ============================================

export interface EntityPageShortcutsOptions {
  /** Handler for adding new record (Ctrl+N) */
  onAdd?: () => void;

  /** Handler for refreshing data (Ctrl+R) */
  onRefresh?: () => void;

  /** Handler for closing/canceling (Escape) */
  onEscape?: () => void;

  /** Handler for saving (Ctrl+S) */
  onSave?: () => void;

  /** Handler for deleting (Ctrl+D or Delete) */
  onDelete?: () => void;

  /** Whether add action is allowed */
  canAdd?: boolean;

  /** Whether refresh action is allowed */
  canRefresh?: boolean;

  /** Whether save action is allowed */
  canSave?: boolean;

  /** Whether delete action is allowed */
  canDelete?: boolean;

  /** Whether shortcuts are enabled */
  enabled?: boolean;
}

/**
 * Preset hook for common entity page shortcuts.
 * Provides Ctrl+N (add), Ctrl+R (refresh), Escape (close), Ctrl+S (save).
 */
export const useEntityPageShortcuts = (
  options: EntityPageShortcutsOptions
): void => {
  const {
    onAdd,
    onRefresh,
    onEscape,
    onSave,
    onDelete,
    canAdd = true,
    canRefresh = true,
    canSave = true,
    canDelete = true,
    enabled = true,
  } = options;

  const shortcuts = useMemo<KeyboardShortcut[]>(() => {
    const result: KeyboardShortcut[] = [];

    // Ctrl+N: Add new record
    if (onAdd) {
      result.push({
        key: "i",
        ctrl: true,
        action: onAdd,
        description: "Add new record",
        enabled: canAdd,
        preventDefault: true,
      });
    }

    // Ctrl+R: Refresh data
    if (onRefresh) {
      result.push({
        key: "r",
        ctrl: true,
        action: onRefresh,
        description: "Refresh data",
        enabled: canRefresh,
        preventDefault: true, // Prevent browser refresh
      });
    }

    // Escape: Close/Cancel
    if (onEscape) {
      result.push({
        key: "Escape",
        action: onEscape,
        description: "Close/Cancel",
        enabled: true,
        preventDefault: false, // Allow default Escape behavior
      });
    }

    // Ctrl+S: Save
    if (onSave) {
      result.push({
        key: "s",
        ctrl: true,
        action: onSave,
        description: "Save",
        enabled: canSave,
        preventDefault: true, // Prevent browser save dialog
      });
    }

    // Delete or Ctrl+D: Delete
    if (onDelete) {
      result.push({
        key: "Delete",
        action: onDelete,
        description: "Delete selected",
        enabled: canDelete,
        preventDefault: true,
      });
    }

    return result;
  }, [
    onAdd,
    onRefresh,
    onEscape,
    onSave,
    onDelete,
    canAdd,
    canRefresh,
    canSave,
    canDelete,
  ]);

  useKeyboardShortcuts({
    shortcuts,
    enabled,
    scope: "entity-page",
  });
};

// ============================================
// Modal/Drawer Shortcuts Preset
// ============================================

export interface ModalShortcutsOptions {
  /** Handler for closing modal (Escape) */
  onClose?: () => void;

  /** Handler for confirming/saving (Ctrl+Enter or Ctrl+S) */
  onConfirm?: () => void;

  /** Whether confirm action is allowed */
  canConfirm?: boolean;

  /** Whether shortcuts are enabled */
  enabled?: boolean;
}

/**
 * Preset hook for modal/drawer shortcuts.
 * Provides Escape (close), Ctrl+S (save/confirm).
 */
export const useModalShortcuts = (options: ModalShortcutsOptions): void => {
  const { onClose, onConfirm, canConfirm = true, enabled = true } = options;

  const shortcuts = useMemo<KeyboardShortcut[]>(() => {
    const result: KeyboardShortcut[] = [];

    // Escape: Close modal
    if (onClose) {
      result.push({
        key: "Escape",
        action: onClose,
        description: "Close",
        enabled: true,
        preventDefault: false,
      });
    }

    // Ctrl+S: Save/Confirm
    if (onConfirm) {
      result.push({
        key: "s",
        ctrl: true,
        action: onConfirm,
        description: "Save/Confirm",
        enabled: canConfirm,
        preventDefault: true,
      });
    }

    // Ctrl+Enter: Alternative confirm
    if (onConfirm) {
      result.push({
        key: "Enter",
        ctrl: true,
        action: onConfirm,
        description: "Confirm",
        enabled: canConfirm,
        preventDefault: true,
      });
    }

    return result;
  }, [onClose, onConfirm, canConfirm]);

  useKeyboardShortcuts({
    shortcuts,
    enabled,
    scope: "modal",
  });
};

// ============================================
// Helper: Get Shortcut Display String
// ============================================

/**
 * Get a human-readable string for a shortcut (e.g., "Ctrl+N" or "⌘N")
 */
export const getShortcutDisplayString = (
  shortcut: Pick<KeyboardShortcut, "key" | "ctrl" | "shift" | "alt">,
  useMacSymbols: boolean = typeof navigator !== "undefined" &&
    navigator.platform.toUpperCase().includes("MAC")
): string => {
  const parts: string[] = [];

  if (shortcut.ctrl) {
    parts.push(useMacSymbols ? "⌘" : "Ctrl");
  }
  if (shortcut.alt) {
    parts.push(useMacSymbols ? "⌥" : "Alt");
  }
  if (shortcut.shift) {
    parts.push(useMacSymbols ? "⇧" : "Shift");
  }

  // Format key name
  let keyDisplay = shortcut.key;
  if (keyDisplay === "Escape") keyDisplay = useMacSymbols ? "⎋" : "Esc";
  else if (keyDisplay === "Enter") keyDisplay = useMacSymbols ? "↵" : "Enter";
  else if (keyDisplay === "Delete") keyDisplay = useMacSymbols ? "⌫" : "Del";
  else keyDisplay = keyDisplay.toUpperCase();

  parts.push(keyDisplay);

  return useMacSymbols ? parts.join("") : parts.join("+");
};
